import React, { useEffect, useRef, useState } from 'react';

import { Box, TableCell } from '@mui/material';

interface ExpandableTableCellProps {
  children: React.ReactNode;
  isExpanded: boolean;
  collapsedHeight?: number;
  onContentExceedsHeight?: (exceeds: boolean) => void;
}

const ExpandableTableCell: React.FC<ExpandableTableCellProps> = ({
  children,
  collapsedHeight = 200,
  isExpanded,
  onContentExceedsHeight,
}) => {
  const [contentHeight, setContentHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const updateContentHeight = () => {
    if (contentRef.current) {
      const currentHeight = contentRef.current.scrollHeight;

      if (currentHeight !== contentHeight) {
        setContentHeight(currentHeight);
      }

      if (onContentExceedsHeight) {
        const exceeds = currentHeight > collapsedHeight;
        onContentExceedsHeight(exceeds);
      }
    }
  };

  useEffect(() => {
    updateContentHeight();

    const handleResize = () => {
      updateContentHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [collapsedHeight]);

  return (
    <TableCell
      sx={{
        position: 'relative',
        overflow: 'hidden',
        verticalAlign: 'top',
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          overflow: 'hidden',
          maxHeight: isExpanded
            ? `${contentHeight + 32}px`
            : `${collapsedHeight}px`,
          transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            overflow: isExpanded ? 'visible' : 'hidden',
            textOverflow: isExpanded ? 'clip' : 'ellipsis',
          }}
        >
          {children}
        </Box>
      </Box>
    </TableCell>
  );
};

export default ExpandableTableCell;
